import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <div className="min-w-[1024px]">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-44 pb-28 overflow-hidden bg-dark">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/c703a368ba1c12baf18f04ba1ff99688.jpg"
              alt="About Eclectic Home Furniture Las Vegas"
              className="w-full h-full object-cover object-center opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/80 to-dark/50" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-6">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-gold" />
              <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">Our Story</span>
            </div>
            <h1 className="font-serif text-6xl md:text-7xl font-bold text-white leading-tight mb-6 max-w-2xl">
              Twenty Years of Beautiful Homes
            </h1>
            <p className="text-white/60 text-lg max-w-xl leading-relaxed">
              Since 2004, Eclectic Home has been helping Las Vegas families create interiors they're 
              genuinely proud of — one carefully chosen piece at a time.
            </p>
          </div>
        </section>

        {/* Story Content */}
        <section className="py-28 bg-cream-DEFAULT">
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[540px]">
              <div className="absolute inset-0 rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20furniture%20showroom%20with%20premium%20curated%20pieces%20beautiful%20interior%20photography%20elegant%20retail%20display%20warm%20lighting%20high-end%20furniture%20brand%20showroom%20atmosphere&width=800&height=900&seq=about2&orientation=portrait"
                  alt="Eclectic Home showroom Las Vegas"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-5 -right-5 bg-gold text-white px-8 py-6 rounded-xl">
                <div className="font-serif text-4xl font-bold">2004</div>
                <div className="text-white/80 text-xs uppercase tracking-widest mt-1">Est. Las Vegas</div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-8 h-px bg-gold" />
                <span className="text-gold text-xs font-semibold uppercase tracking-[0.25em]">How We Started</span>
              </div>
              <h2 className="font-serif text-4xl font-bold text-dark leading-tight mb-6">
                Born from a Passion for <span className="text-gold">Beautiful Living</span>
              </h2>
              <p className="text-dark/60 text-sm leading-relaxed mb-5">
                Eclectic Home began in 2004 when our founder, returning from years traveling through European furniture markets and Asian manufacturing hubs, realized that the Las Vegas market was missing something fundamental: a furniture store that treated quality and design as non-negotiable.
              </p>
              <p className="text-dark/60 text-sm leading-relaxed mb-5">
                We opened our first showroom on the Las Vegas Strip corridor with 80 carefully selected pieces. Today, we carry over 500 items — all chosen through the same obsessive criteria: exceptional craft, honest materials, and design that respects how people actually live.
              </p>
              <p className="text-dark/60 text-sm leading-relaxed mb-10">
                Our team of certified interior designers offers complimentary consultations to every client who walks through our doors. We don't believe in selling furniture — we believe in helping people build homes.
              </p>
              <Link to="/showroom" className="inline-flex items-center gap-3 bg-dark text-white px-8 py-4 rounded text-sm font-bold uppercase tracking-widest hover:bg-gold transition-colors cursor-pointer whitespace-nowrap">
                Visit Our Showroom <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-dark">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="font-serif text-4xl font-bold text-white mb-4">What We Believe</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-award-line', title: 'Quality Over Quantity', desc: 'We\'d rather carry 500 exceptional pieces than 5,000 mediocre ones. Every item earns its place.' },
                { icon: 'ri-heart-line', title: 'Design for Life', desc: 'Good furniture doesn\'t just look beautiful — it works for the way you actually live, day to day.' },
                { icon: 'ri-leaf-line', title: 'Sustainable Sourcing', desc: 'We partner with manufacturers who share our commitment to ethical and sustainable practices.' },
                { icon: 'ri-user-heart-line', title: 'People First', desc: 'Every interaction should feel personal, unhurried, and genuinely helpful. Always.' },
              ].map((v) => (
                <div key={v.title} className="bg-white/5 border border-white/8 rounded-2xl p-8 text-center hover:border-gold/30 transition-colors">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-gold/10 mx-auto mb-5">
                    <i className={`${v.icon} text-gold text-2xl`} />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-white mb-3">{v.title}</h3>
                  <p className="text-white/40 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-24 bg-cream-DEFAULT">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="font-serif text-4xl font-bold text-dark">Our Design Team</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { name: 'Elise Fontaine', role: 'Head of Design', years: '18 yrs', img: 'https://readdy.ai/api/search-image?query=elegant%20professional%20female%20interior%20designer%20smiling%20confident%20neutral%20background%20warm%20studio%20lighting%20headshot%20portrait%20sophisticated&width=400&height=400&seq=tm1&orientation=squarish' },
                { name: 'Marco Delacroix', role: 'Senior Buyer', years: '14 yrs', img: 'https://readdy.ai/api/search-image?query=professional%20male%20furniture%20buyer%20confident%20stylish%20smiling%20neutral%20background%20warm%20lighting%20headshot%20portrait&width=400&height=400&seq=tm2&orientation=squarish' },
                { name: 'Priya Sharma', role: 'Design Consultant', years: '9 yrs', img: 'https://readdy.ai/api/search-image?query=elegant%20professional%20female%20design%20consultant%20smiling%20warm%20neutral%20background%20studio%20lighting%20headshot%20portrait%20friendly&width=400&height=400&seq=tm3&orientation=squarish' },
                { name: 'James Whitfield', role: 'Showroom Director', years: '12 yrs', img: 'https://readdy.ai/api/search-image?query=confident%20professional%20male%20showroom%20director%20smiling%20neutral%20background%20warm%20lighting%20headshot%20portrait%20sophisticated&width=400&height=400&seq=tm4&orientation=squarish' },
              ].map((m) => (
                <div key={m.name} className="text-center group">
                  <div className="w-44 h-44 mx-auto rounded-full overflow-hidden mb-5 border-4 border-cream-dark group-hover:border-gold transition-colors">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-serif text-lg font-bold text-dark">{m.name}</h3>
                  <p className="text-gold text-xs font-semibold uppercase tracking-wider mt-1">{m.role}</p>
                  <p className="text-dark/40 text-xs mt-1">{m.years} experience</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gold py-16">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="font-serif text-3xl font-bold text-white mb-4">Visit Our Las Vegas Showroom</h2>
            <p className="text-white/80 mb-8">Experience our collections in person with a complimentary design consultation.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/showroom" className="bg-white text-gold px-8 py-4 rounded font-bold text-sm uppercase tracking-wider cursor-pointer whitespace-nowrap hover:bg-cream-DEFAULT transition-colors">
                Plan Your Visit
              </Link>
              <Link to="/contact" className="border-2 border-white text-white px-8 py-4 rounded font-bold text-sm uppercase tracking-wider cursor-pointer whitespace-nowrap hover:bg-white/10 transition-colors">
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
